import type { KeyModifier, NormalizedKeyEvent } from "../types";

const normalizeKeyCache = new Map<string, string>();
const printableCharacterCache = new Map<string, boolean>();
const inputElementCache = new WeakMap<Element, boolean>();
const hotkeyFormatCache = new Map<string, string>();
const hotkeyKeyCache = new Map<string, string>();
const platformDetectionCache = { isMac: false, timestamp: 0 };
const PLATFORM_CACHE_TTL = 60000; //* 1 minute (platform rarely changes)

function detectPlatform(): boolean {
  const now = Date.now();
  if (platformDetectionCache.timestamp && now - platformDetectionCache.timestamp < PLATFORM_CACHE_TTL) {
    return platformDetectionCache.isMac;
  }

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  platformDetectionCache.isMac = isMac;
  platformDetectionCache.timestamp = now;
  return isMac;
}

export function normalizeKeyEvent(event: KeyboardEvent): NormalizedKeyEvent {
  const modifiers = new Set<KeyModifier>();

  if (event.ctrlKey) modifiers.add("ctrl");
  if (event.shiftKey) modifiers.add("shift");
  if (event.altKey) modifiers.add("alt");
  if (event.metaKey) modifiers.add("meta");

  return {
    key: normalizeKey(event.key),
    code: event.code,
    modifiers,
    originalEvent: event,
    timestamp: Date.now(),
  };
}

export function normalizeKey(key: string): string {
  const cached = normalizeKeyCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const normalized = key.toLowerCase();

  const keyMap: Record<string, string> = {
    " ": "space",
    spacebar: "space",
    esc: "escape",
    del: "delete",
    ins: "insert",
    left: "arrowleft",
    right: "arrowright",
    up: "arrowup",
    down: "arrowdown",
  };

  const result = keyMap[normalized] || normalized;

  normalizeKeyCache.set(key, result);

  return result;
}

export function isPrintableCharacter(key: string): boolean {
  const cached = printableCharacterCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  let result = false;

  if (key.length === 1) {
    const code = key.charCodeAt(0);
    result =
      (code >= 0x20 && code <= 0x7e) //* ASCII printable
      || (code >= 0xa0 && code <= 0xff); //* Latin-1 supplement
  } else {
    const printableSpecial = ["space", "tab"];
    result = printableSpecial.includes(key.toLowerCase());
  }

  printableCharacterCache.set(key, result);

  return result;
}

export function isInputElement(element: Element | null): boolean {
  if (!element) return false;

  const cached = inputElementCache.get(element);
  if (cached !== undefined) {
    return cached;
  }

  const tagName = element.tagName.toLowerCase();
  const inputLikeTags = ["input", "textarea", "select"];

  let result = false;

  if (inputLikeTags.includes(tagName)) {
    result = true;
  } else {
    if (element.hasAttribute("contenteditable")) {
      const value = element.getAttribute("contenteditable");
      result = value === "true" || value === "";
    }
  }

  inputElementCache.set(element, result);

  return result;
}

export function formatHotkey(key: string, modifiers?: KeyModifier[]): string {
  const cacheKey = `${key}:${(modifiers || []).sort().join(",")}`;

  const cached = hotkeyFormatCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const parts: string[] = [];

  if (modifiers && modifiers.length > 0) {
    const order: KeyModifier[] = ["ctrl", "alt", "shift", "meta"];

    order.forEach((mod) => {
      if (modifiers.includes(mod)) {
        parts.push(formatModifier(mod));
      }
    });
  }

  parts.push(formatKey(key));

  const result = parts.join("+");

  hotkeyFormatCache.set(cacheKey, result);

  return result;
}

function formatModifier(modifier: KeyModifier): string {
  const isMac = detectPlatform();

  const modifierMap: Record<KeyModifier, string> = {
    ctrl: isMac ? "⌃" : "Ctrl",
    alt: isMac ? "⌥" : "Alt",
    shift: isMac ? "⇧" : "Shift",
    meta: isMac ? "⌘" : "Win",
  };

  return modifierMap[modifier];
}

function formatKey(key: string): string {
  const keyMap: Record<string, string> = {
    escape: "Esc",
    delete: "Del",
    insert: "Ins",
    arrowleft: "←",
    arrowright: "→",
    arrowup: "↑",
    arrowdown: "↓",
    space: "Space",
    enter: "Enter",
    tab: "Tab",
    backspace: "Backspace",
  };

  return keyMap[key.toLowerCase()] || key.toUpperCase();
}

export function createHotkeyKey(key: string, modifiers?: KeyModifier[]): string {
  const cacheKey = `${key}:${(modifiers || []).sort().join(",")}`;

  const cached = hotkeyKeyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const normalizedKey = normalizeKey(key);
  const sortedModifiers = (modifiers || []).sort();

  const result = sortedModifiers.length > 0 ? `${sortedModifiers.join("+")}+${normalizedKey}` : normalizedKey;

  hotkeyKeyCache.set(cacheKey, result);

  return result;
}

export function hotkeyMatches(event: NormalizedKeyEvent, key: string, modifiers?: KeyModifier[]): boolean {
  const normalizedKey = normalizeKey(key);

  //* First check: direct key match
  let keyMatches = event.key === normalizedKey;

  /**
   * Fallback: check event.code for modifier combinations that produce special characters
   * e.g., SHIFT+ALT+M on Mac produces '˝' but code is 'KeyM'
   */
  if (!keyMatches && event.code && event.modifiers.size > 0) {
    //* Extract the letter from event.code (e.g., 'KeyM' -> 'm', 'Digit1' -> '1')
    const codeMatch = event.code.match(/^Key([A-Z])$/i) || event.code.match(/^Digit(\d)$/);
    if (codeMatch) {
      const codeKey = codeMatch[1].toLowerCase();
      keyMatches = codeKey === normalizedKey;
    }
  }

  if (!keyMatches) {
    return false;
  }

  const requiredModifiers = new Set(modifiers || []);

  if (event.modifiers.size !== requiredModifiers.size) {
    return false;
  }

  for (const mod of requiredModifiers) {
    if (!event.modifiers.has(mod)) {
      return false;
    }
  }

  return true;
}

export const keyNormalizerPerformance = {
  clearCache(): void {
    normalizeKeyCache.clear();
    printableCharacterCache.clear();
    hotkeyFormatCache.clear();
    hotkeyKeyCache.clear();
    platformDetectionCache.timestamp = 0;
  },

  getCacheStats(): {
    normalizeKey: number;
    printableCharacter: number;
    hotkeyFormat: number;
    hotkeyKey: number;
    platformDetection: boolean;
    totalMemoryEstimate: string;
  } {
    const estimate =
      normalizeKeyCache.size * 20 //* Key string mappings
      + printableCharacterCache.size * 10 //* Boolean values
      + hotkeyFormatCache.size * 30 //* Formatted strings
      + hotkeyKeyCache.size * 25; //* Key strings

    return {
      normalizeKey: normalizeKeyCache.size,
      printableCharacter: printableCharacterCache.size,
      hotkeyFormat: hotkeyFormatCache.size,
      hotkeyKey: hotkeyKeyCache.size,
      platformDetection: platformDetectionCache.timestamp > 0,
      totalMemoryEstimate: `~${(estimate / 1024).toFixed(1)}KB`,
    };
  },

  preloadCommonKeys(): void {
    const commonKeys = [
      "Enter",
      "Escape",
      "Space",
      "Tab",
      "Backspace",
      "Delete",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "F6",
      "F7",
      "F8",
      "F9",
      "F10",
      "F11",
      "F12",
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
      "u",
      "v",
      "w",
      "x",
      "y",
      "z",
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
    ];

    commonKeys.forEach((key) => normalizeKey(key));

    commonKeys.forEach((key) => isPrintableCharacter(key));

    const specialChars = [" ", ".", ",", ";", ":", "'", '"', "[", "]", "{", "}", "(", ")", "+", "-", "=", "_"];
    specialChars.forEach((char) => isPrintableCharacter(char));
  },

  invalidatePlatformCache(): void {
    platformDetectionCache.timestamp = 0;
    hotkeyFormatCache.clear(); //* Format depends on platform
  },
};
